import type { FetchInit } from '@ifixit/ifixit-api-client';
import { SentryError } from '@ifixit/sentry';
import { _js } from '@ifixit/utils/localize';
import { webApiClient } from 'Shared/web_api_client';

export interface ApiRequestInit extends FetchInit {
   /** Status panel text while the call runs; a failure's message replaces it. */
   statusPanelMessage?: string;
}

// A failure rejects with text fit to show a person even when the server gave
// no message: a Varnish 429 is plain text, and a network failure has no body.
export function sendApiRequest(
   method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
   endpoint: string,
   statName: string,
   { statusPanelMessage, ...init }: ApiRequestInit = {}
): Promise<Response> {
   const request = send(method, endpoint, statName, init);
   if (statusPanelMessage) {
      LoadingIndicator.withPromise(request, {
         pendingMessage: statusPanelMessage,
         failureMessage: _js('Error'),
         hideSuccessMessage: true,
         useErrorResponseAsFailureMessage: true,
         duration: 4000,
      });
   }
   return request;
}

async function send(
   method: string,
   endpoint: string,
   statName: string,
   init: FetchInit
): Promise<Response> {
   const client = await webApiClient();
   let response: Response;
   try {
      response = await client.fetch(endpoint, statName, { ...init, method });
   } catch {
      // Offline, or the client's timeout aborted the request
      throw new SentryError(_js('A networking error occurred.'));
   }

   if (!response.ok) {
      throw new SentryError(await failureMessage(response), {
         extra: { endpoint, status: response.status },
      });
   }
   return response;
}

async function failureMessage(response: Response): Promise<string> {
   // The Varnish rate limit answers in plain text, so check the status first
   if (response.status === 429) {
      return _js('You have made too many requests recently.');
   }

   if (response.status === 409) {
      return _js(
         'Your changes could not be saved because the version of the document you are editing is out of date. Reload the page and try again.'
      );
   }

   let body: { message?: unknown; errors?: { message?: unknown }[] } | null;
   try {
      body = await response.json();
   } catch {
      return _js('A networking error occurred.');
   }
   // A validation failure's own message is "Validation failed"; the first
   // error says what failed.
   const message = body?.errors?.[0]?.message ?? body?.message;
   return typeof message === 'string' && message ? message : _js('An unknown error occurred.');
}
